import { memo, useCallback } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import ClientsTable from '../ClientTables';
import type { ClientAction, ClientFormStep } from '../../constants';
import type { ClientGroup, ClientRecord } from '../../../../types/ClientManagementTypes';
import styles from './ClientGroupPanel.module.scss';

interface ClientGroupPanelProps {
  group: ClientGroup;
  isOpen: boolean;
  onToggle: (groupId: number) => void;
  isFiltered: boolean;
  onAction: (action: ClientAction, client: ClientRecord, groupId: number, anchor: HTMLElement | null) => void;
  onOpenClient: (client: ClientRecord, step: ClientFormStep) => void;
}

const ClientGroupPanel = ({ group, isOpen, onToggle, isFiltered, onAction, onOpenClient }: ClientGroupPanelProps) => {
  const regionId = `client-group-${group.id}`;

  // Stable, or the table below rebuilds its column array on every render.
  const handleAction = useCallback(
    (action: ClientAction, client: ClientRecord, anchor: HTMLElement | null) => onAction(action, client, group.id, anchor),
    [group.id, onAction],
  );

  return (
    <section className={styles.panel}>
      <h3 className={styles.headerRow}>
        <button type="button" className={styles.headerButton} aria-expanded={isOpen} aria-controls={regionId} onClick={() => onToggle(group.id)}>
          {isOpen ? <DownOutlined className={styles.chevron} /> : <RightOutlined className={styles.chevron} />}
          {group.name}
        </button>
      </h3>
      {/* The clients arrive with the group, but a closed panel still mounts no
          table: every open one is a live virtual list. */}
      <ConditionalRender visible={isOpen} hideFallback>
        <div id={regionId} className={styles.body}>
          <ClientsTable clients={group.clients} isFiltered={isFiltered} onAction={handleAction} onOpenClient={onOpenClient} />
        </div>
      </ConditionalRender>
    </section>
  );
};

// Holds only while every callback reaching `onAction` stays referentially stable.
export default memo(ClientGroupPanel);
